import { z } from "zod";

/**
 * Broadcasts — one written message, sent to a list.
 *
 * Named apart from the platform's own notifications on purpose: `NotificationSetting` and
 * `NotificationEvent` are the platform noticing something and telling someone. A broadcast is a
 * coordinator writing a message and sending it, to people who need not be applicants at all.
 */

export const BROADCAST_STATUSES = ["DRAFT", "SENDING", "PAUSED", "SENT", "CANCELLED"] as const;
export type BroadcastStatus = (typeof BROADCAST_STATUSES)[number];

/**
 * A merge field key: what appears between the braces in the message.
 *
 * Restricted to the shape a column heading can be normalised into, so `{{Test Password}}` in a
 * spreadsheet and `{{test_password}}` in the message are the same field and nobody has to know
 * which spelling won.
 */
export const mergeFieldKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/, "Use lowercase letters, numbers and underscores");

/**
 * Batching. A list under a hundred goes at once; beyond that the relay chain is the constraint —
 * Mailgun's free tier is a hundred a day and every relay meters itself, so a burst moves the
 * failure downstream rather than avoiding it.
 */
export const broadcastScheduleSchema = z
  .object({
    batchSize: z.number().int().min(1).max(1_000).nullable().optional(),
    batchIntervalMinutes: z.number().int().min(5).max(1_440).nullable().optional(),
  })
  .refine(
    (value) =>
      // Both or neither: a batch size with no interval would release one batch and stop forever,
      // which looks exactly like the send having silently failed.
      (value.batchSize == null) === (value.batchIntervalMinutes == null),
    { message: "Set both a batch size and an interval, or neither.", path: ["batchSize"] },
  );

export const broadcastCreateRequestSchema = z.object({
  programmeId: z.string().min(1),
  formId: z.string().min(1).nullable().optional(),
  name: z.string().trim().min(1).max(200),
  subject: z.string().trim().min(1).max(300),
});

export const broadcastUpdateRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    subject: z.string().trim().min(1).max(300).optional(),
    bodyMarkup: z.string().max(50_000).optional(),
    buttonLabel: z.string().trim().max(80).nullable().optional(),
    /** Merge fields are allowed in the URL, so this cannot be `z.string().url()`. */
    buttonUrl: z.string().trim().max(2_000).nullable().optional(),
    /** Copied on an individual resend only — see the column's comment in schema.prisma. */
    resendCcEmail: z.string().trim().email().max(320).nullable().optional(),
    batchSize: z.number().int().min(1).max(1_000).nullable().optional(),
    batchIntervalMinutes: z.number().int().min(5).max(1_440).nullable().optional(),
    formId: z.string().min(1).nullable().optional(),
  })
  .refine(
    (value) =>
      // Both keys together or neither, and then both set or both cleared. A size with no interval
      // releases one batch and stops forever, which is indistinguishable from a silent failure —
      // and on a PATCH the stored value of the other half is not visible here to fall back on.
      (value.batchSize === undefined) === (value.batchIntervalMinutes === undefined) &&
      (value.batchSize == null) === (value.batchIntervalMinutes == null),
    { message: "Set both a batch size and an interval, or neither.", path: ["batchSize"] },
  )
  .refine((value) => !value.buttonLabel?.trim() || Boolean(value.buttonUrl?.trim()), {
    message: "A button needs a link.",
    path: ["buttonUrl"],
  });

/**
 * One recipient. `fields` carries whatever extra columns the list had — username, password, a
 * venue, a time — keyed by normalised column name.
 */
export const broadcastRecipientRowSchema = z.object({
  fullName: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(320),
  fields: z.record(z.string().max(2_000)).default({}),
});

export const broadcastRecipientImportRequestSchema = z
  .object({
    rows: z.array(broadcastRecipientRowSchema).max(10_000).optional(),
    /** Typed or pasted straight into the screen. Parsed on the server, like every other route in. */
    text: z.string().max(1_000_000).optional(),
    apply: z.boolean().default(false),
    /** Replace the whole list rather than merging into it. */
    replace: z.boolean().default(false),
  })
  .refine((value) => Boolean(value.rows?.length) || Boolean(value.text?.trim()), {
    message: "Provide rows, text, or a CSV file.",
    path: ["rows"],
  });

export const broadcastRecipientListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.enum(["all", "pending", "sent", "failed"]).default("all"),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

/**
 * `send` starts or resumes and only ever reaches people still waiting — so adding names to a
 * finished broadcast and pressing send again mails the new ones and nobody else.
 *
 * `resend_all` is the deliberate exception: it puts *everyone* back to waiting, including people
 * who already received the message. That is a real thing to want — a batch of test credentials
 * that went out with the wrong link — and it is the one action here that can put a second copy of
 * a message in somebody's inbox, so it is named for what it does rather than hidden behind `send`.
 */
export const broadcastActionRequestSchema = z.object({
  action: z.enum(["send", "pause", "resume", "cancel", "resend_all"]),
});

export type BroadcastCreateRequest = z.infer<typeof broadcastCreateRequestSchema>;
export type BroadcastUpdateRequest = z.infer<typeof broadcastUpdateRequestSchema>;
export type BroadcastRecipientRow = z.infer<typeof broadcastRecipientRowSchema>;
export type BroadcastRecipientImportRequest = z.infer<typeof broadcastRecipientImportRequestSchema>;
export type BroadcastRecipientListQuery = z.infer<typeof broadcastRecipientListQuerySchema>;
export type BroadcastActionRequest = z.infer<typeof broadcastActionRequestSchema>;
